import { useNoteStore } from '@/features/roi/NotesDrawer'
import Td2 from '@/components/zthd/Td2'
import { BookmarkFilledIcon } from '@radix-ui/react-icons'
import MyLink from '@/components/zthd/my-link'
import { getWeek } from '@/lib/date-utils'
import { useOldNotes, useNewNotes } from '@/features/roi/NotesDrawer'
import VisibleDateTrackerCell from './VisibleDateTrackerCell'
import { getDrBaselineAtDate, getCostBaseline, getProfitBaseline } from '@/lib/dr-utils'
import { formatUSD } from '@/lib/format-utils'
import dayjs from 'dayjs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useContrastModeStore } from '@/components/roi/ContrastModeSelector'
import {
  getBaselinePackageKey,
  hasDrBaselineStartDate,
} from './day-roas-utils'

const DateCell = ({ noteDates, ...props }) => (
  noteDates === undefined
    ? <DateCellWithNotes {...props} />
    : <DateCellContent {...props} hasNotes={noteDates.has(String(props.row.original.date))} />
)

// Other ROI tables retain their existing per-date loading until they adopt the summary.
const DateCellWithNotes = (props) => {
  const date = props.row.original.date
  const oldNotes = useOldNotes(date)
  const newNotes = useNewNotes(date)
  return <DateCellContent {...props} hasNotes={oldNotes.length > 0 || newNotes.length > 0} />
}

const DateCellContent = ({ row, pid, country, drBaselines, pidForBaseline, costBaselines, profitBaselines, packageName = null, hasNotes }) => {
  const original = row.original
  const date = original.date

  const [, week] = getWeek(date)
  const showNotes = useNoteStore((state) => state.showNotes)
  const contrastEnabled = useContrastModeStore((state) => state.enabled)

  const dateStr = String(date)
  const dateFormatted = `${dateStr.substring(0, 4)}-${dateStr.substring(
    4,
    6,
  )}-${dateStr.substring(6, 8)}`
  
  // 格式化对比日期
  const contrastDate = original.contrast?.date
  const contrastDateFormatted = contrastDate 
    ? `${String(contrastDate).substring(0, 4)}-${String(contrastDate).substring(4, 6)}-${String(contrastDate).substring(6, 8)}`
    : null
  const [, contrastWeek] = contrastDate ? getWeek(contrastDate) : [null, null]

  const packageKey = getBaselinePackageKey(pidForBaseline, packageName)
  const day0StartDate = hasDrBaselineStartDate({
    drBaselines,
    day: 0,
    country,
    packageKey,
    dateFormatted,
  })
  const day3StartDate = hasDrBaselineStartDate({
    drBaselines,
    day: 3,
    country,
    packageKey,
    dateFormatted,
  })
  const costStartDate =
    costBaselines?.[country]?.[pidForBaseline]?.[dateFormatted] !== undefined
  const profitStartDate =
    profitBaselines?.[country]?.[pidForBaseline]?.[dateFormatted] !== undefined

  const hasChange = day0StartDate || day3StartDate || costStartDate || profitStartDate

  const prevDate = dayjs(date, 'YYYYMMDD').subtract(1, 'day').format('YYYYMMDD')
  const day0Baseline = getDrBaselineAtDate(
    drBaselines,
    0,
    country,
    pidForBaseline,
    date,
    packageName,
  )
  const prevDay0Baseline = getDrBaselineAtDate(
    drBaselines,
    0,
    country,
    pidForBaseline,
    prevDate,
    packageName,
  )
  const day3Baseline = getDrBaselineAtDate(
    drBaselines,
    3,
    country,
    pidForBaseline,
    date,
    packageName,
  )
  const prevDay3Baseline = getDrBaselineAtDate(
    drBaselines,
    3,
    country,
    pidForBaseline,
    prevDate,
    packageName,
  )

  const costBaseline = getCostBaseline(
    costBaselines,
    country,
    pidForBaseline,
    date,
  )
  const prevCostBaseline = getCostBaseline(
    costBaselines,
    country,
    pidForBaseline,
    prevDate,
  )
  const profitBaseline = getProfitBaseline(
    profitBaselines,
    country,
    pidForBaseline,
    date,
  )
  const prevProfitBaseline = getProfitBaseline(
    profitBaselines,
    country,
    pidForBaseline,
    prevDate,
  )

  return (
    <VisibleDateTrackerCell date={date}>
      <Td2
        className="relative pl-5" // 增加内边距并设为相对定位容器
        line2={
          <div className="flex flex-col gap-1 items-end">
            <div className="flex gap-1 justify-end items-center">
              {hasNotes && (
                <BookmarkFilledIcon
                  className="text-primary"
                  onClick={() => showNotes({ date })}
                />
              )}
              <span>(周{week})</span>
            </div>
            {contrastEnabled && contrastDateFormatted && (
              <div className="text-xs text-muted-foreground">
                {contrastDateFormatted} (周{contrastWeek})
              </div>
            )}
          </div>
        }
      >
        {hasChange && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute bottom-1 left-1 w-3 h-3 bg-green-500 rounded-full" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Baseline Changed:</p>
                {day0StartDate && (
                  <p>
                    DR Day 0: {prevDay0Baseline} -&gt; {day0Baseline}
                  </p>
                )}
                {day3StartDate && (
                  <p>
                    DR Day 3: {prevDay3Baseline} -&gt; {day3Baseline}
                  </p>
                )}
                {costStartDate && (
                  <p>
                    Cost: {formatUSD(prevCostBaseline, 0, 0)} -&gt; {formatUSD(costBaseline, 0, 0)}
                  </p>
                )}
                {profitStartDate && (
                  <p>
                    Profit: {formatUSD(prevProfitBaseline, 0, 0)} -&gt; {formatUSD(profitBaseline, 0, 0)}
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <MyLink to={`cc/${pid}/${date}`}>{date}</MyLink>
      </Td2>
    </VisibleDateTrackerCell>
  )
}

export default DateCell
